import type { WhatsappSignupConfig } from '../api/types';

/**
 * Meta's WhatsApp embedded signup, driven through the Facebook JS SDK — the
 * same flow sphere-connect runs. `FB.login()` resolves an auth `code` while the
 * WABA identifiers (waba_id, phone_number_id) arrive separately over a
 * `postMessage` event from the popup — order isn't guaranteed, so both are held
 * and the promise settles once both are present.
 *
 * Rejects on SDK-load or signup errors. The window listener is scoped to a
 * single run, so this is safe to call from any click handler without lifecycle
 * wiring.
 */

/**
 * Every event Meta uses to announce a finished signup. Handling only some of
 * them is indistinguishable from a hang — the auth code arrives, its other half
 * never does, and the tenant watches a spinner after a signup that succeeded.
 */
const FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
]);

/**
 * The popup's auth code expires 30s after Meta mints it, so waiting past that
 * for the other half of the handshake cannot succeed anyway — and without a
 * deadline, an event we fail to recognise strands the caller forever.
 */
const HANDSHAKE_TIMEOUT_MS = 30_000;

/**
 * Only Facebook's own hosts may complete the handshake. A suffix test against
 * 'facebook.com' also accepts 'https://notfacebook.com', a host anyone can
 * register — and whoever posts a matching FINISH decides which WABA the auth
 * code is spent against, so this has to be a whole-host match.
 */
const FACEBOOK_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*facebook\.com$/;

export interface EmbeddedSignupDone {
  status: 'done';
  code: string;
  wabaId: string;
  /** Empty when Meta doesn't include it (WhatsApp Business app onboarding). */
  phoneNumberId: string;
  /** The Meta business portfolio the WABA sits under; empty on events that omit it. */
  businessId: string;
}

/** Popup closed. `step` is Meta's name for the screen they backed out of. */
export interface EmbeddedSignupCancelled {
  status: 'cancelled';
  step: string | null;
}

export type EmbeddedSignupOutcome = EmbeddedSignupDone | EmbeddedSignupCancelled;

export interface FbLoginResponse {
  status?: string;
  authResponse?: { code?: string; accessToken?: string } | null;
  error?: string;
}

/**
 * The two shapes FB.login is called with here: a Facebook Login for Business
 * configuration (WhatsApp signup, and Pages when one is configured) or a scope
 * list (classic Facebook Login, the Page dialog's fallback).
 */
export interface FbLoginOptions {
  config_id?: string;
  scope?: string;
  response_type?: string;
  override_default_response_type?: boolean;
  extras?: Record<string, unknown>;
}

export interface FacebookSdk {
  init(options: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FbLoginResponse) => void, options: FbLoginOptions): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

/**
 * The version the *popup* runs on, deliberately NOT config.apiVersion. That value
 * pins the backend's graph.facebook.com calls, where staying on a known-good
 * version is worth something; here it picks which dialog the SDK opens
 * (/<version>/dialog/oauth), and embedded signup v4 is only served by a current
 * one — an old dialog signs the admin in and returns a code without ever running
 * the WhatsApp wizard, so the session info half of the handshake never exists.
 * Meta's own implementation sample tracks the latest version for exactly this.
 */
const SDK_VERSION = 'v25.0';

let sdkLoading: Promise<void> | null = null;

const loadSdkScript = (): Promise<void> => {
  if (window.FB) return Promise.resolve();
  if (sdkLoading) return sdkLoading;
  sdkLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoading = null; // allow a retry after a network hiccup
      reject(new Error('The Facebook SDK could not be loaded — check the network and try again.'));
    };
    document.head.appendChild(script);
  });
  return sdkLoading;
};

/**
 * Load + init the SDK. Idempotent; call it as soon as the connect screen knows
 * embedded signup is available, so the click handler's `FB.login` opens the
 * popup immediately instead of tripping popup blockers on a late script load.
 */
export const setupFacebookSdk = async (config: Pick<WhatsappSignupConfig, 'appId'>): Promise<void> => {
  if (!config.appId) throw new Error('Facebook sign-in is not configured.');
  await loadSdkScript();
  await new Promise<void>((resolve) => {
    const init = () => {
      window.FB?.init({
        appId: config.appId as string,
        autoLogAppEvents: true,
        xfbml: true,
        version: SDK_VERSION,
      });
      resolve();
    };
    if (window.FB) init();
    else window.fbAsyncInit = init;
  });
};

interface SignupMessage {
  type?: string;
  event?: string;
  data?: {
    business_id?: string;
    waba_id?: string;
    phone_number_id?: string;
    /** Present on CANCEL: the screen the tenant backed out of. */
    current_step?: string;
    /** Present on CANCEL when Meta is reporting a failure rather than an abort. */
    error_message?: string;
  };
  error_message?: string;
}

const parseSignupMessage = (event: MessageEvent): SignupMessage | null => {
  if (!FACEBOOK_ORIGIN.test(event.origin)) return null;
  try {
    const data: unknown =
      typeof event.data === 'string'
        ? JSON.parse(event.data)
        : typeof event.data === 'object' && event.data !== null
          ? event.data
          : null;
    if (data && (data as SignupMessage).type === 'WA_EMBEDDED_SIGNUP') return data as SignupMessage;
  } catch {
    // Non-JSON messages from facebook.com frames are none of our business.
  }
  return null;
};

const openLoginDialog = (configurationId: string): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('The Facebook SDK is not ready — try again.'));
      return;
    }
    window.FB.login(
      (response) => {
        if (response.authResponse?.code) resolve(response.authResponse.code);
        else if (response.error) reject(new Error(response.error));
        else reject(new Error('Login cancelled'));
      },
      {
        config_id: configurationId,
        response_type: 'code',
        override_default_response_type: true,
        // Everything the retired versions passed here — feature type, session info
        // version — now lives in the Facebook Login for Business configuration
        // that config_id points at. Sending them back pins us to a flow Meta
        // switches off on 15 October 2026.
        extras: { setup: {} },
      },
    );
  });

export const runEmbeddedSignup = (config: WhatsappSignupConfig): Promise<EmbeddedSignupOutcome> => {
  if (!config.available || !config.configurationId) {
    return Promise.reject(new Error('Embedded signup is not configured.'));
  }
  return new Promise((resolve, reject) => {
    let authCode: string | null = null;
    let business: SignupMessage['data'] | null = null;
    let settled = false;
    let deadline: ReturnType<typeof setTimeout> | null = null;
    /** Every WA_EMBEDDED_SIGNUP event seen this run, for the timeout log line. */
    const seen: string[] = [];

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (deadline) clearTimeout(deadline);
      window.removeEventListener('message', onMessage);
      fn();
    };

    /**
     * Which half is missing IS the diagnosis, and "try again" is the wrong advice
     * for the cause that actually strands people: a config_id naming a plain
     * Facebook Login for Business configuration, or one built for a retired
     * signup version, signs the admin in and mints a code but never runs the
     * WhatsApp wizard — so the session info it would post does not exist, and
     * every retry fails the same way.
     */
    const timeoutMessage = (): string => {
      if (authCode && !business) {
        return (
          'Facebook signed you in but never sent your WhatsApp account details. The login ' +
          `configuration (${config.configurationId}) is most likely not an Embedded Signup ` +
          'configuration, or was built for a retired signup version — recreate it in the Meta app ' +
          'dashboard and update WHATSAPP_CONFIGURATION_ID.'
        );
      }
      if (business && !authCode) {
        return 'Facebook sent your WhatsApp details but no sign-in code — please try again.';
      }
      return 'The Facebook signup did not finish in time — please try again.';
    };

    /** Armed by whichever half of the handshake lands first. */
    const armDeadline = () => {
      if (deadline) return;
      deadline = setTimeout(() => {
        // The events Meta did post are what separate a wrong configuration from a
        // finish event we failed to recognise; neither is visible from the UI copy.
        console.warn(
          `[whatsapp-signup] handshake timed out — code=${authCode ? 'yes' : 'no'} ` +
            `sessionInfo=${business ? 'yes' : 'no'} events=[${seen.join(', ') || 'none'}]`,
        );
        settle(() => reject(new Error(timeoutMessage())));
      }, HANDSHAKE_TIMEOUT_MS);
    };

    // Both arrive asynchronously and in no fixed order.
    const resolveIfReady = () => {
      const wabaId = business?.waba_id;
      if (!authCode || !wabaId) return;
      const code = authCode;
      settle(() =>
        resolve({
          status: 'done',
          code,
          wabaId,
          phoneNumberId: business?.phone_number_id || '',
          businessId: business?.business_id || '',
        }),
      );
    };

    const onMessage = (event: MessageEvent) => {
      const message = parseSignupMessage(event);
      if (!message) return;
      seen.push(message.event || '(no event)');
      if (message.event && FINISH_EVENTS.has(message.event)) {
        // waba_id is the only identifier every finish event carries — business_id
        // is absent from several of them, and demanding it fails a good signup.
        if (!message.data?.waba_id) {
          settle(() => reject(new Error('Meta returned incomplete signup data — try again.')));
          return;
        }
        business = message.data;
        armDeadline();
        resolveIfReady();
      } else if (message.event === 'CANCEL') {
        // Meta reports a *failed* signup as a CANCEL carrying error_message, not as
        // a distinct error event. Reading every CANCEL as a clean abort throws away
        // the one string that says why, and shows "nothing was saved" for a failure
        // the tenant has to act on.
        const failure = message.data?.error_message || message.error_message;
        if (failure) {
          settle(() => reject(new Error(failure)));
          return;
        }
        settle(() => resolve({ status: 'cancelled', step: message.data?.current_step ?? null }));
      } else if (message.event === 'ERROR' || message.event === 'error') {
        settle(() =>
          reject(
            new Error(
              message.error_message || message.data?.error_message || 'The WhatsApp signup failed.',
            ),
          ),
        );
      }
    };

    window.addEventListener('message', onMessage);

    (async () => {
      try {
        await setupFacebookSdk(config);
        authCode = await openLoginDialog(config.configurationId as string);
        armDeadline();
        resolveIfReady();
      } catch (error) {
        // FB.login rejects with 'Login cancelled' when the popup is dismissed —
        // that's a cancel, not an error.
        if (error instanceof Error && error.message === 'Login cancelled') {
          settle(() => resolve({ status: 'cancelled', step: null }));
        } else {
          settle(() => reject(error instanceof Error ? error : new Error(String(error))));
        }
      }
    })();
  });
};
